/**
 * NotificationBadge Component
 *
 * Shows a badge with count and severity indicator for notifications.
 * Includes pulsing animation when scanning is in progress.
 *
 * @module shared/components/MobileTaskbar
 */

import styles from './NotificationBadge.module.css';

interface NotificationBadgeProps {
  count: number;
  severity: 'critical' | 'warning' | 'info' | null;
  isScanning?: boolean;
}

export function NotificationBadge({ count, severity, isScanning }: NotificationBadgeProps) {
  // Don't show badge if no count and not scanning
  if (count === 0 && !isScanning) {
    return null;
  }

  // Show scanning indicator
  if (isScanning && count === 0) {
    return (
      <span className={`${styles.badge} ${styles.scanning}`}>
        <span className={styles.pulsingDot} />
      </span>
    );
  }

  // Determine badge color based on severity
  const severityClass = severity
    ? styles[severity]
    : styles.info;

  return (
    <span className={`${styles.badge} ${severityClass} ${isScanning ? styles.scanning : ''}`}>
      {isScanning && <span className={styles.pulsingDot} />}
      {count > 0 && (
        <span className={styles.count}>
          {count > 99 ? '99+' : count}
        </span>
      )}
    </span>
  );
}

export default NotificationBadge;
